// src/components/HistoryView.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BACKEND_URL } from '../config';
import HistoryChart from './HistoryChart';

/**
 * Combined HistoryView (no feature loss)
 * - Keeps original data loading + merge behavior:
 *   - /api/history/{userId}?days=N (app history)
 *   - merges /api/wearables/synced-data?days=N (device fields)
 *   - weight tab uses /api/weight/history + merges synced wearable weight if present
 * - Uses dropdown range selector
 * - Tab column mapping:
 *   - food: Calories + Protein + Carbs + Fats + Water + Hydration (if present)
 *   - exercise: wearable fields + sleep/HR + Oura fields + app exercise fields (if present)
 *   - overview: keeps legacy calorie summary
 */
function HistoryView({ userId, token, type = 'overview', showMealHistory = false }) {
  const [history, setHistory] = useState([]);
  const [weightMeta, setWeightMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(7);
  const [mealHistory, setMealHistory] = useState([]);
  const [mealHistoryLoading, setMealHistoryLoading] = useState(false);

  const title =
    type === 'food'
      ? 'Food History'
      : type === 'exercise'
      ? 'Exercise History'
      : type === 'weight'
      ? 'Weight History'
      : 'History';

  function normalizeDate(isoOrDate) {
    if (!isoOrDate) return null;
    if (
      typeof isoOrDate === 'string' &&
      isoOrDate.length >= 10 &&
      isoOrDate[4] === '-' &&
      isoOrDate[7] === '-'
    ) {
      return isoOrDate.slice(0, 10);
    }
    try {
      return new Date(isoOrDate).toISOString().slice(0, 10);
    } catch {
      return null;
    }
  }

  const presetRanges = useMemo(
    () => [
      { label: '7 Days', value: 7 },
      { label: '14 Days', value: 14 },
      { label: '30 Days', value: 30 },
      { label: '90 Days', value: 90 },
      { label: '6 Months', value: 180 },
      { label: '1 Year', value: 365 },
      { label: 'All Time', value: 3650 },
    ],
    []
  );

  const consumedFor = useCallback((d) => Number(d?.calories_consumed || 0), []);
  const burnedFor = useCallback((d) => Number(d?.calories_burned || 0), []);
  const netFor = useCallback((d) => (consumedFor(d) || 0) - (burnedFor(d) || 0), [consumedFor, burnedFor]);

  const loadSyncedWearablesRange = useCallback(async () => {
    if (!token) return [];
    try {
      const sRes = await fetch(`${BACKEND_URL}/api/wearables/synced-data?days=${dateRange}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!sRes.ok) return [];
      const sData = await sRes.json();
      return Array.isArray(sData.synced_data) ? sData.synced_data : [];
    } catch {
      return [];
    }
  }, [token, dateRange]);

  const mergeSyncedIntoHistory = useCallback((baseHistory, syncedRows) => {
    const byDate = new Map();
    for (const d of Array.isArray(baseHistory) ? baseHistory : []) {
      const key = normalizeDate(d.date);
      if (!key) continue;
      byDate.set(key, { ...d, date: key });
    }

    for (const s of Array.isArray(syncedRows) ? syncedRows : []) {
      const key = normalizeDate(s.date || s.synced_at);
      if (!key) continue;

      const cur = byDate.get(key) || { date: key };
      const merged = { ...cur };

      const fields = [
        "steps",
        "calories_burned",
        "active_minutes",
        "distance_km",
        "floors",
        "sedentary_minutes",
        "sleep_minutes",
        "sleep_efficiency",
        "sleep_score",
        "deep_sleep_minutes",
        "rem_sleep_minutes",
        "readiness_score",
        "hrv_balance",
        "activity_score",
        "resting_heart_rate",
        "avg_heart_rate",
        "heart_rate",
        "systolic",
        "diastolic",
        "hydration_percent",
      ];

      for (const f of fields) {
        const v = s?.[f];
        if (v !== null && v !== undefined) merged[f] = v;
      }

      if (s?.source && !merged.wearable_source) merged.wearable_source = s.source;

      byDate.set(key, merged);
    }

    return Array.from(byDate.values()).sort((a, b) => (a.date < b.date ? -1 : 1));
  }, []);

  const loadCaloriesHistory = useCallback(async () => {
    if (!userId || !token) return;

    const response = await fetch(`${BACKEND_URL}/api/history/${userId}?days=${dateRange}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      const txt = await response.text();
      throw new Error(txt || 'Failed to load history');
    }

    const data = await response.json();
    const base = data.history || [];

    const synced = await loadSyncedWearablesRange();
    const merged = mergeSyncedIntoHistory(base, synced);

    setHistory(merged);
    setWeightMeta(null);
  }, [userId, token, dateRange, loadSyncedWearablesRange, mergeSyncedIntoHistory]);

  const isDeviceSource = (src) => {
    if (!src) return false;
    const s = String(src).toLowerCase();
    return [
      'withings',
      'fitbit',
      'oura',
      'healthkit',
      'health_connect',
      'samsung_health',
      'samsung',
      'apple_watch',
      'garmin',
    ].includes(s);
  };

  const loadWeightHistory = useCallback(async () => {
    if (!userId || !token) return;

    const wRes = await fetch(`${BACKEND_URL}/api/weight/history/${userId}?days=${dateRange}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!wRes.ok) {
      const txt = await wRes.text();
      throw new Error(txt || 'Failed to load weight history');
    }

    const wData = await wRes.json();
    const logs = wData.logs || [];

    let synced = [];
    try {
      const sRes = await fetch(`${BACKEND_URL}/api/wearables/synced-data?days=${dateRange}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (sRes.ok) {
        const sData = await sRes.json();
        synced = sData.synced_data || [];
      }
    } catch {}

    const byDate = new Map();

    const ensure = (date) => {
      if (!byDate.has(date)) {
        byDate.set(date, {
          date,
          weight_manual: null,
          weight_device: null,
          wearable_source: null,
          _manual_ts: null,
          _device_ts: null,
        });
      }
      return byDate.get(date);
    };

    for (const log of logs) {
      const date = normalizeDate(log.date || log.created_at);
      if (!date) continue;

      const entry = ensure(date);
      const ts = (log.created_at && Date.parse(log.created_at)) || 0;
      const src = log.source || null;
      const w = typeof log.weight === 'number' ? log.weight : Number(log.weight);
      if (!Number.isFinite(w)) continue;

      if (isDeviceSource(src)) {
        if (!entry._device_ts || ts >= entry._device_ts) {
          entry.weight_device = w;
          entry.wearable_source = src || entry.wearable_source;
          entry._device_ts = ts;
        }
      } else {
        if (!entry._manual_ts || ts >= entry._manual_ts) {
          entry.weight_manual = w;
          entry._manual_ts = ts;
        }
      }
    }

    for (const d of synced) {
      const date = normalizeDate(d.date || d.synced_at);
      if (!date) continue;
      const w = typeof d.weight === 'number' ? d.weight : Number(d.weight);
      if (!Number.isFinite(w)) continue;

      const entry = ensure(date);
      if (entry.weight_device == null) {
        entry.weight_device = w;
        entry.wearable_source = d.source || entry.wearable_source;
      }
    }

    const merged = Array.from(byDate.values())
      .map((x) => {
        // eslint-disable-next-line no-unused-vars
        const { _manual_ts, _device_ts, ...rest } = x;
        return rest;
      })
      .sort((a, b) => (a.date < b.date ? -1 : 1));

    setHistory(merged);
    setWeightMeta({
      goal_weight: wData.goal_weight ?? null,
      starting_weight: wData.starting_weight ?? null,
      current_weight: wData.current_weight ?? null,
      total_entries: wData.total_entries ?? null,
    });
  }, [userId, token, dateRange]);


  const loadMealHistory = useCallback(async () => {
    if (!userId || !token || type !== 'food' || !showMealHistory) {
      setMealHistory([]);
      return;
    }

    setMealHistoryLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/nutrition/meal-feedback/history/${userId}?days=${dateRange}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || 'Failed to load meal history');
      }

      const data = await res.json();
      const items =
        data?.history ||
        data?.meal_feedback ||
        data?.items ||
        data?.logs ||
        [];

      setMealHistory(Array.isArray(items) ? items : []);
    } catch (e) {
      console.error('Error loading meal history:', e);
      setMealHistory([]);
    } finally {
      setMealHistoryLoading(false);
    }
  }, [userId, token, type, showMealHistory, dateRange]);

  const loadHistory = useCallback(async () => {
    if (!userId || !token) return;
    setLoading(true);
    try {
      if (type === 'weight') await loadWeightHistory();
      else await loadCaloriesHistory();
    } catch (e) {
      console.error('Error loading history:', e);
      setHistory([]);
      setWeightMeta(null);
    } finally {
      setLoading(false);
    }
  }, [userId, token, type, loadCaloriesHistory, loadWeightHistory]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    loadMealHistory();
  }, [loadMealHistory]);

  const totalConsumed = useMemo(() => history.reduce((sum, d) => sum + consumedFor(d), 0), [history, consumedFor]);
  const totalBurned = useMemo(() => history.reduce((sum, d) => sum + burnedFor(d), 0), [history, burnedFor]);
  const avgConsumed = useMemo(() => (history.length > 0 ? totalConsumed / history.length : 0), [history.length, totalConsumed]);
  const avgBurned = useMemo(() => (history.length > 0 ? totalBurned / history.length : 0), [history.length, totalBurned]);

  const totalSteps = useMemo(() => history.reduce((sum, d) => sum + (d.steps || 0), 0), [history]);
  const avgSteps = useMemo(() => (history.length > 0 ? totalSteps / history.length : 0), [history.length, totalSteps]);
  const sleepDaysCount = useMemo(() => history.filter((d) => d.sleep_minutes).length, [history]);
  const avgSleepMinutes = useMemo(() => {
    if (sleepDaysCount <= 0) return 0;
    return history.reduce((sum, d) => sum + (d.sleep_minutes || 0), 0) / sleepDaysCount;
  }, [history, sleepDaysCount]);
  const daysWithWearableData = useMemo(() => history.filter((d) => d.steps || d.sleep_minutes || d.sleep_score).length, [history]);

  const hasVal = useCallback((field) => history.some((d) => d?.[field] !== null && d?.[field] !== undefined), [history]);

  // Food columns
  const showFoodCalories = type === 'food';
  const showProtein = type === 'food' && hasVal('protein_g');
  const showCarbs = type === 'food' && hasVal('carbs_g');
  const showFats = type === 'food' && hasVal('fats_g');
  const showWaterOz = type === 'food' && hasVal('water_oz');
  const showHydrationFood = type === 'food' && hasVal('hydration_percent');

  // Exercise columns
  const showBurned = type === 'exercise';
  const showSteps = type === 'exercise' && hasVal('steps');
  const showWearActive = type === 'exercise' && hasVal('active_minutes');
  const showWearDistance = type === 'exercise' && hasVal('distance_km');
  const showWearFloors = type === 'exercise' && hasVal('floors');
  const showWearSedentary = type === 'exercise' && hasVal('sedentary_minutes');

  const showWearSleepMinutes = type === 'exercise' && hasVal('sleep_minutes');
  const showWearSleepEfficiency = type === 'exercise' && hasVal('sleep_efficiency');
  const showWearRestHR = type === 'exercise' && hasVal('resting_heart_rate');
  const showWearAvgHR = type === 'exercise' && hasVal('avg_heart_rate');

  const showOuraActivityScore = type === 'exercise' && hasVal('activity_score');
  const showOuraReadiness = type === 'exercise' && hasVal('readiness_score');
  const showOuraSleepScore = type === 'exercise' && hasVal('sleep_score');
  const showOuraDeep = type === 'exercise' && hasVal('deep_sleep_minutes');
  const showOuraRem = type === 'exercise' && hasVal('rem_sleep_minutes');
  const showOuraHrv = type === 'exercise' && hasVal('hrv_balance');

  const showAppExerciseCals = type === 'exercise' && hasVal('exercise_calories_burned');
  const showAppExerciseMins = type === 'exercise' && hasVal('exercise_minutes');

  // Weight stats
  const latestWeight = useMemo(() => {
    if (weightMeta?.current_weight != null) return Number(weightMeta.current_weight);
    const last = history.length ? history[history.length - 1] : null;
    return last?.weight_manual ?? last?.weight_device ?? null;
  }, [weightMeta, history]);

  const earliestWeight = useMemo(() => {
    if (weightMeta?.starting_weight != null) return Number(weightMeta.starting_weight);
    const first = history.length ? history[0] : null;
    return first?.weight_manual ?? first?.weight_device ?? null;
  }, [weightMeta, history]);

  const deltaWeight = useMemo(() => {
    if (typeof latestWeight === 'number' && typeof earliestWeight === 'number') return latestWeight - earliestWeight;
    return null;
  }, [latestWeight, earliestWeight]);

  const deviceDays = useMemo(() => history.filter((d) => typeof d.weight_device === 'number').length, [history]);
  const manualDays = useMemo(() => history.filter((d) => typeof d.weight_manual === 'number').length, [history]);

  const getDaySource = (day) => day?.wearable_source || day?.source || null;


  const mealHistoryCount = Array.isArray(mealHistory) ? mealHistory.length : 0;
  const avgMealRating = useMemo(() => {
    const vals = (mealHistory || []).map((x) => Number(x?.rating)).filter((n) => Number.isFinite(n) && n > 0);
    if (!vals.length) return 0;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  }, [mealHistory]);

  const renderMealStars = (rating) => {
    const r = Math.max(0, Math.min(5, Number(rating) || 0));
    return "★".repeat(r) + "☆".repeat(5 - r);
  };

  if (loading) {
    return (
      <div className="history-view">
        <h1 className="heading-2">{title}</h1>
        <p style={{ textAlign: 'center', padding: '48px', color: 'var(--text-secondary)' }}>Loading...</p>
      </div>
    );
  }

  return (
    <div className="history-view">
      <div className="ct-header">
        <h1 className="heading-2" style={{ margin: 0 }}>{title}</h1>

        <div className="ct-range">
          <label className="ct-label">Range</label>
          <select
            className="form-input ct-select"
            value={dateRange}
            onChange={(e) => setDateRange(Number(e.target.value))}
            data-testid="range-select"
          >
            {presetRanges.map((range) => (
              <option key={range.value} value={range.value}>{range.label}</option>
            ))}
          </select>
        </div>
      </div>

      {history.length === 0 ? (
        <div className="card ct-card ct-empty">
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
          <p className="body-large" style={{ color: 'var(--text-muted)' }}>No history data for this period.</p>
        </div>
      ) : (
        <>
          {/* Summary */}
          {type !== 'weight' && (
            <div className="ct-stats-grid">
              {type === 'food' && (
                <>
                  <div className="card ct-stat">
                    <div className="body-small" style={{ color: 'var(--text-secondary)' }}>Total Calories</div>
                    <div className="heading-3" style={{ color: 'var(--success-color)' }}>{totalConsumed.toLocaleString()}</div>
                    <div className="body-small" style={{ color: 'var(--text-muted)' }}>calories</div>
                  </div>
                  <div className="card ct-stat">
                    <div className="body-small" style={{ color: 'var(--text-secondary)' }}>Avg / day</div>
                    <div className="heading-3">{avgConsumed.toFixed(0)}</div>
                    <div className="body-small" style={{ color: 'var(--text-muted)' }}>cal/day</div>
                  </div>
                  {showMealHistory && (
                    <>
                      <div className="card ct-stat">
                        <div className="body-small" style={{ color: 'var(--text-secondary)' }}>Rated Meals</div>
                        <div className="heading-3">{mealHistoryCount}</div>
                        <div className="body-small" style={{ color: 'var(--text-muted)' }}>entries</div>
                      </div>
                      <div className="card ct-stat">
                        <div className="body-small" style={{ color: 'var(--text-secondary)' }}>Avg Rating</div>
                        <div className="heading-3">{mealHistoryCount ? avgMealRating.toFixed(1) : '-'}</div>
                        <div className="body-small" style={{ color: 'var(--text-muted)' }}>out of 5</div>
                      </div>
                    </>
                  )}
                </>
              )}

              {type === 'exercise' && (
                <>
                  <div className="card ct-stat">
                    <div className="body-small" style={{ color: 'var(--text-secondary)' }}>Total Burned</div>
                    <div className="heading-3" style={{ color: 'var(--warning-color)' }}>{totalBurned.toLocaleString()}</div>
                    <div className="body-small" style={{ color: 'var(--text-muted)' }}>calories</div>
                  </div>
                  <div className="card ct-stat">
                    <div className="body-small" style={{ color: 'var(--text-secondary)' }}>Avg / day</div>
                    <div className="heading-3">{avgBurned.toFixed(0)}</div>
                    <div className="body-small" style={{ color: 'var(--text-muted)' }}>cal/day</div>
                  </div>

                  {daysWithWearableData > 0 && (
                    <>
                      <div className="card ct-stat">
                        <div className="body-small" style={{ color: 'var(--text-secondary)' }}>Total Steps</div>
                        <div className="heading-3" style={{ color: '#3b82f6' }}>{totalSteps.toLocaleString()}</div>
                        <div className="body-small" style={{ color: 'var(--text-muted)' }}>steps</div>
                      </div>

                      <div className="card ct-stat">
                        <div className="body-small" style={{ color: 'var(--text-secondary)' }}>Avg Steps/Day</div>
                        <div className="heading-3" style={{ color: '#3b82f6' }}>{avgSteps.toFixed(0)}</div>
                        <div className="body-small" style={{ color: 'var(--text-muted)' }}>steps</div>
                      </div>

                      {avgSleepMinutes > 0 && (
                        <div className="card ct-stat">
                          <div className="body-small" style={{ color: 'var(--text-secondary)' }}>Avg Sleep</div>
                          <div className="heading-3" style={{ color: '#8b5cf6' }}>{(avgSleepMinutes / 60).toFixed(1)}</div>
                          <div className="body-small" style={{ color: 'var(--text-muted)' }}>hours</div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}

              {type === 'overview' && (
                <>
                  <div className="card ct-stat">
                    <div className="body-small" style={{ color: 'var(--text-secondary)' }}>Avg Intake</div>
                    <div className="heading-3">{avgConsumed.toFixed(0)}</div>
                    <div className="body-small" style={{ color: 'var(--text-muted)' }}>cal/day</div>
                  </div>
                  <div className="card ct-stat">
                    <div className="body-small" style={{ color: 'var(--text-secondary)' }}>Avg Burn</div>
                    <div className="heading-3">{avgBurned.toFixed(0)}</div>
                    <div className="body-small" style={{ color: 'var(--text-muted)' }}>cal/day</div>
                  </div>
                </>
              )}
            </div>
          )}

          {type === 'weight' && (
            <div className="ct-stats-grid">
              <div className="card ct-stat">
                <div className="body-small" style={{ color: 'var(--text-secondary)' }}>Current</div>
                <div className="heading-3">{typeof latestWeight === 'number' ? latestWeight.toFixed(1) : '-'}</div>
                <div className="body-small" style={{ color: 'var(--text-muted)' }}>lbs</div>
              </div>

              <div className="card ct-stat">
                <div className="body-small" style={{ color: 'var(--text-secondary)' }}>Change</div>
                <div className="heading-3">{typeof deltaWeight === 'number' ? `${deltaWeight > 0 ? '+' : ''}${deltaWeight.toFixed(1)}` : '-'}</div>
                <div className="body-small" style={{ color: 'var(--text-muted)' }}>lbs</div>
              </div>

              <div className="card ct-stat">
                <div className="body-small" style={{ color: 'var(--text-secondary)' }}>Goal</div>
                <div className="heading-3">{weightMeta?.goal_weight != null ? Number(weightMeta.goal_weight).toFixed(1) : '-'}</div>
                <div className="body-small" style={{ color: 'var(--text-muted)' }}>lbs</div>
              </div>

              <div className="card ct-stat">
                <div className="body-small" style={{ color: 'var(--text-secondary)' }}>Entries</div>
                <div className="heading-3">{history.length}</div>
                <div className="body-small" style={{ color: 'var(--text-muted)' }}>{manualDays} manual • {deviceDays} device</div>
              </div>
            </div>
          )}

          {/* Chart */}
          <div className="card ct-card ct-card-lg mb-6" style={{ padding: '24px', marginBottom: '24px' }}>
            <HistoryChart history={history} type={type} />
          </div>


          {type === 'food' && showMealHistory && (
            <div className="card ct-card ct-card-lg" style={{ padding: '24px', marginBottom: '24px' }}>
              <h3 className="heading-4" style={{ marginBottom: '16px' }}>Meal History & Ratings</h3>

              {mealHistoryLoading ? (
                <p style={{ color: 'var(--text-secondary)' }}>Loading meal history…</p>
              ) : mealHistory.length === 0 ? (
                <p className="body-medium" style={{ color: 'var(--text-muted)' }}>No meal ratings found for this period.</p>
              ) : (
                <div className="ct-table-wrap" style={{ overflowX: 'auto' }}>
                  <table className="ct-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid var(--border-light)' }}>
                        <th style={{ textAlign: 'left', padding: '12px 8px' }}>Date</th>
                        <th style={{ textAlign: 'left', padding: '12px 8px' }}>Meal</th>
                        <th style={{ textAlign: 'left', padding: '12px 8px' }}>Type</th>
                        <th style={{ textAlign: 'center', padding: '12px 8px' }}>Rating</th>
                        <th style={{ textAlign: 'left', padding: '12px 8px' }}>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mealHistory.slice().reverse().slice(0, 100).map((item, idx) => (
                        <tr key={item.id || item.feedback_id || item.log_id || `${item.date}-${idx}`} style={{ borderBottom: '1px solid var(--border-light)' }}>
                          <td style={{ padding: '12px 8px', whiteSpace: 'nowrap' }}>
                            {item.date
                              ? new Date(item.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                              : '-'}
                          </td>
                          <td style={{ padding: '12px 8px' }}>
                            {item.meal_name || item.title || item.food_name || item.recipe_name || '-'}
                          </td>
                          <td style={{ padding: '12px 8px', textTransform: 'capitalize' }}>
                            {item.meal_type || '-'}
                          </td>
                          <td style={{ textAlign: 'center', padding: '12px 8px', color: '#f59e0b', whiteSpace: 'nowrap' }}>
                            {renderMealStars(item.rating)}{item.rating ? ` (${Number(item.rating).toFixed(0)})` : ''}
                          </td>
                          <td style={{ padding: '12px 8px', color: 'var(--text-secondary)' }}>
                            {item.note || item.notes || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Table */}
          <div className="card ct-card ct-card-lg" style={{ padding: '24px' }}>
            <h3 className="heading-4" style={{ marginBottom: '16px' }}>Detailed Log</h3>

            <div className="ct-table-wrap" style={{ overflowX: 'auto' }}>
              {type === 'weight' ? (
                <table className="ct-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border-light)' }}>
                      <th style={{ textAlign: 'left', padding: '12px 8px' }}>Date</th>
                      <th style={{ textAlign: 'right', padding: '12px 8px' }}>Manual</th>
                      <th style={{ textAlign: 'right', padding: '12px 8px' }}>Device</th>
                      <th style={{ textAlign: 'center', padding: '12px 8px' }}>Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.slice().reverse().slice(0, 60).map((day) => {
                      const src = getDaySource(day);
                      return (
                        <tr key={day.date} style={{ borderBottom: '1px solid var(--border-light)' }}>
                          <td style={{ padding: '12px 8px' }}>
                            {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          </td>
                          <td style={{ textAlign: 'right', padding: '12px 8px' }}>
                            {typeof day.weight_manual === 'number' ? day.weight_manual.toFixed(1) : '-'}
                          </td>
                          <td style={{ textAlign: 'right', padding: '12px 8px' }}>
                            {typeof day.weight_device === 'number' ? day.weight_device.toFixed(1) : '-'}
                          </td>
                          <td style={{ textAlign: 'center', padding: '12px 8px' }}>
                            {src ? <span className="ct-pill">{String(src).replace(/_/g,' ')}</span> : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <table className="ct-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border-light)' }}>
                      <th style={{ textAlign: 'left', padding: '12px 8px' }}>Date</th>

                      {/* Food */}
                      {showFoodCalories && <th style={{ textAlign: 'right', padding: '12px 8px' }}>Calories</th>}
                      {showProtein && <th style={{ textAlign: 'right', padding: '12px 8px' }}>Protein</th>}
                      {showCarbs && <th style={{ textAlign: 'right', padding: '12px 8px' }}>Carbs</th>}
                      {showFats && <th style={{ textAlign: 'right', padding: '12px 8px' }}>Fats</th>}
                      {showWaterOz && <th style={{ textAlign: 'right', padding: '12px 8px' }}>Water</th>}
                      {showHydrationFood && <th style={{ textAlign: 'right', padding: '12px 8px' }}>Hyd%</th>}

                      {/* Exercise */}
                      {showBurned && <th style={{ textAlign: 'right', padding: '12px 8px' }}>Burned</th>}
                      {showSteps && <th style={{ textAlign: 'right', padding: '12px 8px' }}>Steps</th>}
                      {showWearActive && <th style={{ textAlign: 'right', padding: '12px 8px' }}>Active</th>}
                      {showWearDistance && <th style={{ textAlign: 'right', padding: '12px 8px' }}>Distance</th>}
                      {showWearFloors && <th style={{ textAlign: 'right', padding: '12px 8px' }}>Floors</th>}
                      {showWearSedentary && <th style={{ textAlign: 'right', padding: '12px 8px' }}>Sedentary</th>}

                      {showWearSleepMinutes && <th style={{ textAlign: 'right', padding: '12px 8px' }}>Sleep</th>}
                      {showWearSleepEfficiency && <th style={{ textAlign: 'right', padding: '12px 8px' }}>Eff%</th>}
                      {showWearRestHR && <th style={{ textAlign: 'right', padding: '12px 8px' }}>Rest HR</th>}
                      {showWearAvgHR && <th style={{ textAlign: 'right', padding: '12px 8px' }}>Avg HR</th>}

                      {showOuraActivityScore && <th style={{ textAlign: 'right', padding: '12px 8px' }}>Act Score</th>}
                      {showOuraReadiness && <th style={{ textAlign: 'right', padding: '12px 8px' }}>Readiness</th>}
                      {showOuraSleepScore && <th style={{ textAlign: 'right', padding: '12px 8px' }}>Sleep Score</th>}
                      {showOuraDeep && <th style={{ textAlign: 'right', padding: '12px 8px' }}>Deep</th>}
                      {showOuraRem && <th style={{ textAlign: 'right', padding: '12px 8px' }}>REM</th>}
                      {showOuraHrv && <th style={{ textAlign: 'right', padding: '12px 8px' }}>HRV</th>}

                      {showAppExerciseCals && <th style={{ textAlign: 'right', padding: '12px 8px' }}>Ex Cal</th>}
                      {showAppExerciseMins && <th style={{ textAlign: 'right', padding: '12px 8px' }}>Ex Min</th>}

                      <th style={{ textAlign: 'center', padding: '12px 8px' }}>Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.slice().reverse().slice(0, 60).map((day) => {
                      const src = getDaySource(day);
                      return (
                        <tr key={day.date} style={{ borderBottom: '1px solid var(--border-light)' }}>
                          <td style={{ padding: '12px 8px' }}>
                            {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          </td>

                          {showFoodCalories && <td style={{ textAlign: 'right', padding: '12px 8px', color: 'var(--success-color)' }}>{Number(day.calories_consumed || 0).toLocaleString()}</td>}
                          {showProtein && <td style={{ textAlign: 'right', padding: '12px 8px' }}>{day.protein_g != null ? Number(day.protein_g).toFixed(0) : '-'}</td>}
                          {showCarbs && <td style={{ textAlign: 'right', padding: '12px 8px' }}>{day.carbs_g != null ? Number(day.carbs_g).toFixed(0) : '-'}</td>}
                          {showFats && <td style={{ textAlign: 'right', padding: '12px 8px' }}>{day.fats_g != null ? Number(day.fats_g).toFixed(0) : '-'}</td>}
                          {showWaterOz && <td style={{ textAlign: 'right', padding: '12px 8px' }}>{day.water_oz != null ? Number(day.water_oz).toFixed(0) : '-'}</td>}
                          {showHydrationFood && <td style={{ textAlign: 'right', padding: '12px 8px' }}>{day.hydration_percent != null ? Number(day.hydration_percent).toFixed(0) : '-'}</td>}

                          {showBurned && <td style={{ textAlign: 'right', padding: '12px 8px', color: 'var(--warning-color)' }}>{Number(day.calories_burned || 0).toLocaleString()}</td>}
                          {showSteps && <td style={{ textAlign: 'right', padding: '12px 8px', color: '#3b82f6' }}>{day.steps != null ? Number(day.steps).toLocaleString() : '-'}</td>}
                          {showWearActive && <td style={{ textAlign: 'right', padding: '12px 8px' }}>{day.active_minutes != null ? Number(day.active_minutes).toFixed(0) : '-'}</td>}
                          {showWearDistance && <td style={{ textAlign: 'right', padding: '12px 8px' }}>{day.distance_km != null ? Number(day.distance_km).toFixed(2) : '-'}</td>}
                          {showWearFloors && <td style={{ textAlign: 'right', padding: '12px 8px' }}>{day.floors != null ? Number(day.floors).toFixed(0) : '-'}</td>}
                          {showWearSedentary && <td style={{ textAlign: 'right', padding: '12px 8px' }}>{day.sedentary_minutes != null ? Number(day.sedentary_minutes).toFixed(0) : '-'}</td>}

                          {showWearSleepMinutes && <td style={{ textAlign: 'right', padding: '12px 8px', color: '#8b5cf6' }}>{day.sleep_minutes != null ? `${(Number(day.sleep_minutes) / 60).toFixed(1)}h` : '-'}</td>}
                          {showWearSleepEfficiency && <td style={{ textAlign: 'right', padding: '12px 8px' }}>{day.sleep_efficiency != null ? Number(day.sleep_efficiency).toFixed(0) : '-'}</td>}
                          {showWearRestHR && <td style={{ textAlign: 'right', padding: '12px 8px' }}>{day.resting_heart_rate != null ? Number(day.resting_heart_rate).toFixed(0) : '-'}</td>}
                          {showWearAvgHR && <td style={{ textAlign: 'right', padding: '12px 8px' }}>{day.avg_heart_rate != null ? Number(day.avg_heart_rate).toFixed(0) : '-'}</td>}

                          {showOuraActivityScore && <td style={{ textAlign: 'right', padding: '12px 8px' }}>{day.activity_score != null ? Number(day.activity_score).toFixed(0) : '-'}</td>}
                          {showOuraReadiness && <td style={{ textAlign: 'right', padding: '12px 8px' }}>{day.readiness_score != null ? Number(day.readiness_score).toFixed(0) : '-'}</td>}
                          {showOuraSleepScore && <td style={{ textAlign: 'right', padding: '12px 8px' }}>{day.sleep_score != null ? Number(day.sleep_score).toFixed(0) : '-'}</td>}
                          {showOuraDeep && <td style={{ textAlign: 'right', padding: '12px 8px' }}>{day.deep_sleep_minutes != null ? Number(day.deep_sleep_minutes).toFixed(0) : '-'}</td>}
                          {showOuraRem && <td style={{ textAlign: 'right', padding: '12px 8px' }}>{day.rem_sleep_minutes != null ? Number(day.rem_sleep_minutes).toFixed(0) : '-'}</td>}
                          {showOuraHrv && <td style={{ textAlign: 'right', padding: '12px 8px' }}>{day.hrv_balance != null ? Number(day.hrv_balance).toFixed(2) : '-'}</td>}

                          {showAppExerciseCals && <td style={{ textAlign: 'right', padding: '12px 8px' }}>{day.exercise_calories_burned != null ? Number(day.exercise_calories_burned).toFixed(0) : '-'}</td>}
                          {showAppExerciseMins && <td style={{ textAlign: 'right', padding: '12px 8px' }}>{day.exercise_minutes != null ? Number(day.exercise_minutes).toFixed(0) : '-'}</td>}

                          <td style={{ textAlign: 'center', padding: '12px 8px' }}>
                            {src ? <span className="ct-pill">{String(src).replace(/_/g,' ')}</span> : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default HistoryView;
